// Centralized storage utilities with proper error handling and type safety

export class StorageError extends Error {
  constructor(message: string, public originalError?: Error) {
    super(message);
    this.name = 'StorageError';
  }
}

// Safe JSON parsing with error handling
export const safeJsonParse = <T>(value: string | null, fallback: T): T => {
  if (!value) return fallback;
  
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    console.warn('Failed to parse JSON from storage:', error);
    return fallback;
  }
};

// Safe JSON stringifying with error handling
export const safeJsonStringify = (value: unknown): string | null => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    console.error('Failed to stringify value for storage:', error);
    return null;
  }
};

// Generic storage getter with type safety
export const getStorageItem = <T>(key: string, fallback: T): T => {
  try {
    const item = localStorage.getItem(key);
    return safeJsonParse(item, fallback);
  } catch (error) {
    console.error(`Failed to get item '${key}' from storage:`, error);
    return fallback;
  }
};

// Generic storage setter with error handling
export const setStorageItem = (key: string, value: unknown): boolean => {
  try {
    const serialized = safeJsonStringify(value);
    if (serialized === null) return false;
    
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error(`Failed to set item '${key}' in storage:`, error);
    return false;
  }
};

// Remove storage item with error handling
export const removeStorageItem = (key: string): boolean => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Failed to remove item '${key}' from storage:`, error);
    return false;
  }
};

// Clear all storage items
export const clearStorage = (): boolean => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Failed to clear storage:', error);
    return false;
  }
};

// Storage keys constants
export const STORAGE_KEYS = {
  TOKEN: 'token',
  USER_PROFILE: 'userProfile',
  ONBOARDING_DATA: 'onboardingData',
  ONBOARDING_COMPLETE: 'onboardingComplete',
  INSIGHTS_POPUP_SHOWN: 'insightsPopupShown',
  THINKMATE_ONBOARDED: 'thinkmateOnboarded',
} as const;

// Type-safe storage helpers for specific data types
export const storage = {
  // Token management
  getToken: (): string | null => {
    try {
      return localStorage.getItem(STORAGE_KEYS.TOKEN);
    } catch {
      return null;
    }
  },
  
  setToken: (token: string): boolean => {
    return setStorageItem(STORAGE_KEYS.TOKEN, token);
  },
  
  removeToken: (): boolean => {
    return removeStorageItem(STORAGE_KEYS.TOKEN);
  },
  
  // User profile management
  getUserProfile: <T = Record<string, unknown>>(fallback: T): T => {
    return getStorageItem(STORAGE_KEYS.USER_PROFILE, fallback);
  },
  
  setUserProfile: (profile: unknown): boolean => {
    return setStorageItem(STORAGE_KEYS.USER_PROFILE, profile);
  },
  
  removeUserProfile: (): boolean => {
    return removeStorageItem(STORAGE_KEYS.USER_PROFILE);
  },
  
  // Onboarding data management
  getOnboardingData: <T = Record<string, unknown>>(fallback: T): T => {
    return getStorageItem(STORAGE_KEYS.ONBOARDING_DATA, fallback);
  },
  
  setOnboardingData: (data: unknown): boolean => {
    return setStorageItem(STORAGE_KEYS.ONBOARDING_DATA, data);
  },
  
  removeOnboardingData: (): boolean => {
    return removeStorageItem(STORAGE_KEYS.ONBOARDING_DATA);
  },
  
  // Check-in completion tracking
  getCheckInCompletion: (dateKey: string): boolean => {
    return getStorageItem(`checkin_${dateKey}`, false);
  },
  
  setCheckInCompletion: (dateKey: string, completed: boolean): boolean => {
    return setStorageItem(`checkin_${dateKey}`, completed);
  },
  
  // UI state management
  getInsightsPopupShown: (): boolean => {
    return getStorageItem(STORAGE_KEYS.INSIGHTS_POPUP_SHOWN, false);
  },
  
  setInsightsPopupShown: (shown: boolean): boolean => {
    return setStorageItem(STORAGE_KEYS.INSIGHTS_POPUP_SHOWN, shown);
  },
  
  getThinkmateOnboarded: (): boolean => {
    return getStorageItem(STORAGE_KEYS.THINKMATE_ONBOARDED, false);
  },
  
  setThinkmateOnboarded: (onboarded: boolean): boolean => {
    return setStorageItem(STORAGE_KEYS.THINKMATE_ONBOARDED, onboarded);
  },
}; 